import * as React from 'react';

import { kebabCase } from 'lodash';
import styled from 'styled-components';

import CCPA_icon from 'assets/privacyoptions.svg';

import { FooterData } from 'components/Layout/Footer/FooterData';
import FooterSeparator from 'components/Layout/Footer/components/FooterSeparator';
import Link from 'components/Link';
import { LinkWithIcon } from 'components/LinkWithIcon';

import { sanitizeAndUpdatePreviewPaths } from 'utils/textUtils';

const LegalContainer = styled.div`
  color: ${(props) => props.theme.colors.gray};
`;

const LegalCopy = styled.div`
  font-size: 0.917rem;
  font-weight: 500;
`;

const DisclaimerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75em;
  font-weight: 400;
  font-family: ${(props) => props.theme.fonts.default};
  color: ${(props) => props.theme.colors.transparentGray};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    flex-direction: column-reverse;
    grid-row-gap: 2rem;
  }

  .buttonLink {
    text-decoration: underline;
  }

  a,
  .buttonLink {
    color: ${(props) => props.theme.colors.transparentGray};
    transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);

    &:hover {
      color: ${(props) => props.theme.colors.white};
    }
  }
`;

const DisclaimerFooterLinks = styled.div`
  grid-column-gap: 0.5rem;
  grid-row-gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
  display: flex;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    grid-row-gap: 1.5rem;
  }
`;

const ButtonSeparator = styled.span`
  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    display: none;
  }
`;

interface LegalProps {
  data: FooterData;
}

const YPC_BUTTON_TEXT = 'Your Privacy Choices';

const LegalSection: React.FC<LegalProps> = ({ data }) => {
  const handleCookieBannerOpen = () => window.OneTrust?.ToggleInfoDisplay();

  return (
    <LegalContainer>
      <FooterSeparator $top={'1.95em'} $bottom={'2.07em'} data-testId={'footer-separator-2'} />
      {data.pageBody?.map((block, blockIndex) =>
        block?.textBlock?.textColumns?.map((column, columnIndex) => (
          <div key={`${blockIndex}-${columnIndex}`}>
            <LegalCopy
              dangerouslySetInnerHTML={{ __html: sanitizeAndUpdatePreviewPaths(column.body) }}
              data-testId={'footer-legal-copy'}
            />
            <FooterSeparator $bottom={'2rem'} data-testId={'footer-separator-3'} />
            <DisclaimerFooter>
              <div data-testId={'footer-copyright'}>&copy; 2025 tastyfx</div>
              <DisclaimerFooterLinks>
                <>
                  <span key={`${blockIndex}-${columnIndex}-0-link`}>
                    <LinkWithIcon
                      iconUrl={CCPA_icon}
                      clickHandler={handleCookieBannerOpen}
                      dataTestId={`footer-link-${kebabCase(YPC_BUTTON_TEXT)}`}
                    >
                      {YPC_BUTTON_TEXT}
                    </LinkWithIcon>
                  </span>
                  {column.buttonCta?.map((button, buttonIndex) => (
                    <React.Fragment key={buttonIndex}>
                      <ButtonSeparator key={`${blockIndex}-${columnIndex}-${buttonIndex}-sep`}>|</ButtonSeparator>
                      <span key={`${blockIndex}-${columnIndex}-${buttonIndex + 1}-link`}>
                        <Link
                          to={button.buttonLink[0]?.linkUrl?.href || ''}
                          data-testId={`footer-link-${kebabCase(button.buttonText)}`}
                        >
                          {button.buttonText}
                        </Link>
                      </span>
                    </React.Fragment>
                  ))}
                </>
              </DisclaimerFooterLinks>
            </DisclaimerFooter>
          </div>
        ))
      )}
    </LegalContainer>
  );
};

export default LegalSection;
